import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDoctorById } from '../services/doctorsApi';
import { getArticles, formatDuration } from '../services/articlesApi';

/* ─── Helpers shared with rest of file ─────────────────────────── */
const StarRating = ({ value = 4.5 }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const filled = i <= Math.floor(value);
    const half = !filled && i === Math.ceil(value) && value % 1 >= 0.5;
    stars.push(
      <span key={i} style={{ fontSize: 16, color: filled || half ? '#f59e0b' : '#e5e7eb' }}>
        {filled ? '★' : half ? '½' : '☆'}
      </span>
    );
  }
  return <span style={{ display: 'inline-flex', gap: 2 }}>{stars}</span>;
};

const InfoRow = ({ icon, label, value }) => (
  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
    <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{icon}</span>
    <div>
      <div className="t-meta" style={{ marginBottom: 2 }}>{label}</div>
      <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 13.5, color: '#1e1b4b' }}>{value || '—'}</div>
    </div>
  </div>
);

/* ─── Content type config ───────────────────────────────────────── */
const TYPE_META = {
  ARTICLE:    { label: 'Article',     icon: '📄', color: '#4f46e5', bg: '#eef2ff',  border: '#c7d2fe' },
  VIDEO:      { label: 'Video',       icon: '🎬', color: '#dc2626', bg: '#fef2f2',  border: '#fca5a5' },
  WHITEPAPER: { label: 'White Paper', icon: '📋', color: '#0891b2', bg: '#ecfeff',  border: '#a5f3fc' },
};

const TYPE_FILTERS = [
  { value: 'ALL',        label: 'All' },
  { value: 'ARTICLE',    label: 'Articles' },
  { value: 'VIDEO',      label: 'Videos' },
  { value: 'WHITEPAPER', label: 'White Papers' },
];

/* ─── Single publication card ───────────────────────────────────── */
const PublicationCard = ({ item, onClick }) => {
  const meta = TYPE_META[item.type] || TYPE_META.ARTICLE;

  const dateStr = item.publishedDate
    ? new Date(item.publishedDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  const viewsStr = item.viewCount >= 1000
    ? `${(item.viewCount / 1000).toFixed(1)}k`
    : String(item.viewCount ?? 0);

  return (
    <div className="pub-card" onClick={onClick}>
      <div className="pub-card__thumb">
        {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" />}
        {item.type === 'VIDEO' && (
          <div className="pub-card__thumb-overlay">
            <div style={{
              width: 22, height: 22, borderRadius: '50%',
              background: 'rgba(255,255,255,0.92)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 2,
            }}>
              <svg width="9" height="10" viewBox="0 0 9 10" fill="#1e1b4b"><path d="M1 1l7 4-7 4V1z"/></svg>
            </div>
          </div>
        )}
      </div>

      <div className="pub-card__body">
        <div className="pub-card__meta">
          <span style={{
            fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 9999,
            background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
            fontFamily: 'DM Sans, sans-serif',
          }}>
            {meta.icon} {meta.label}
          </span>
          {item.type === 'VIDEO' && item.videoDurationSecs && (
            <span className="t-meta">⏱ {formatDuration(item.videoDurationSecs)}</span>
          )}
          {item.type !== 'VIDEO' && item.readTimeMinutes && (
            <span className="t-meta">⏱ {item.readTimeMinutes} min read</span>
          )}
        </div>

        <div className="pub-card__title">{item.title}</div>

        <p className="pub-card__summary">{item.summary}</p>

        <div className="pub-card__footer">
          {item.tags?.slice(0, 3).map(tag => (
            <span key={tag} className="tag-chip">{tag}</span>
          ))}
          <span className="t-meta" style={{ marginLeft: 'auto' }}>
            {dateStr}{viewsStr ? ` · 👁 ${viewsStr}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ─── Publications skeleton ─────────────────────────────────────── */
const PubSkeleton = () => (
  <div style={{ display: 'flex', gap: 14, padding: '14px 16px', borderRadius: 14, border: '1px solid #f3f4f6', background: '#fff' }}>
    <div className="skeleton" style={{ width: 80, height: 60, borderRadius: 10, flexShrink: 0 }} />
    <div style={{ flex: 1 }}>
      <div className="skeleton skeleton-line" style={{ width: '20%', height: 16, borderRadius: 9999, marginBottom: 8 }} />
      <div className="skeleton skeleton-line" style={{ width: '75%', height: 13, marginBottom: 6 }} />
      <div className="skeleton skeleton-line" style={{ width: '90%', height: 11, marginBottom: 6 }} />
      <div className="skeleton skeleton-line" style={{ width: '90%', height: 11, marginBottom: 8 }} />
      <div style={{ display: 'flex', gap: 5 }}>
        {[40, 50, 45].map(w => (
          <div key={w} className="skeleton" style={{ width: w, height: 16, borderRadius: 9999 }} />
        ))}
      </div>
    </div>
  </div>
);

/* ─── Publications section ──────────────────────────────────────── */
const PublicationsSection = ({ doctorId }) => {
  const navigate = useNavigate();
  const [articles, setArticles]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeFilter, setFilter]   = useState('ALL');

  useEffect(() => {
    setLoading(true);
    getArticles(null, doctorId)
      .then(data => { setArticles(data); setLoading(false); })
      .catch(() => { setArticles([]); setLoading(false); });
  }, [doctorId]);

  const filtered = activeFilter === 'ALL'
    ? articles
    : articles.filter(a => a.type === activeFilter);

  if (!loading && articles.length === 0) return null;

  const counts = {
    ALL:        articles.length,
    ARTICLE:    articles.filter(a => a.type === 'ARTICLE').length,
    VIDEO:      articles.filter(a => a.type === 'VIDEO').length,
    WHITEPAPER: articles.filter(a => a.type === 'WHITEPAPER').length,
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <h3 className="t-h3" style={{ margin: 0 }}>📚 Publications & Content</h3>
        {!loading && articles.length > 0 && (
          <span className="badge">
            {articles.length} {articles.length === 1 ? 'item' : 'items'}
          </span>
        )}
      </div>

      {/* Type filter tabs */}
      {!loading && articles.length > 0 && (
        <div className="pill-filter-row">
          {TYPE_FILTERS.filter(f => f.value === 'ALL' || counts[f.value] > 0).map(f => {
            const active = activeFilter === f.value;
            return (
              <button
                key={f.value}
                className={`pill-filter${active ? ' pill-filter--active' : ''}`}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
                {counts[f.value] > 0 && (
                  <span className="pill-filter__count">{counts[f.value]}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <PubSkeleton key={i} />)
          : filtered.length === 0
            ? (
              <div style={{
                textAlign: 'center', padding: '24px 0',
                color: '#9ca3af', fontFamily: 'DM Sans, sans-serif', fontSize: 13,
              }}>
                No {activeFilter === 'ALL' ? 'content' : TYPE_META[activeFilter]?.label.toLowerCase() + 's'} published yet.
              </div>
            )
            : filtered.map((item, i) => (
              <div key={item.id} style={{ animation: `fadeUp .3s ${i * 40}ms ease both` }}>
                <PublicationCard
                  item={item}
                  onClick={() => navigate(`/articles/${item.id}`)}
                />
              </div>
            ))
        }
      </div>
    </div>
  );
};

/* ─── Main DoctorProfile component ─────────────────────────────── */
const DoctorProfile = () => {
  const navigate = useNavigate();
  const { doctorId } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') === 'walk-in' ? 'walk-in' : 'online';
  const { user } = useAuth();

  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getDoctorById(doctorId, mode)
      .then(d => { setDoctor(d); setLoading(false); })
      .catch(() => { setError('Could not load doctor profile.'); setLoading(false); });
  }, [doctorId, mode]);

  const handleBook = () => {
    const target = `/services/doctors/${doctorId}/book?mode=${mode}`;
    if (!user) { navigate('/login', { state: { from: target } }); return; }
    navigate(target);
  };

  const handleSecondOpinion = () => {
    const target = `/services/doctors/${doctorId}/book?mode=${mode}&type=second-opinion`;
    if (!user) { navigate('/login', { state: { from: target } }); return; }
    navigate(target);
  };

  if (loading) {
    return (
      <div className="page">
        <main className="page-main">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[['30%', 24], ['50%', 16], ['100%', 180]].map(([w, h], i) => (
              <div key={i} className="skeleton" style={{ width: w, height: h, borderRadius: 12 }} />
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div className="page">
        <main className="page-main">
          <div className="error-state"><span>⚠️</span> {error || 'Doctor not found.'}</div>
          <button className="btn-primary" style={{ maxWidth: 240, marginTop: 16 }} onClick={() => navigate(-1)}>
            Go back
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate('/services/online-consultation')}>Doctors</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate(`/services/doctors?mode=${mode}`)}>
            {mode === 'online' ? 'Online' : 'Walk In'}
          </button>
          <span>›</span>
          <span className="bc-current">{doctor.name}</span>
        </div>

        <div className="page-two-col fade-up">

          {/* Left: profile card */}
          <div>
            <div className="card" style={{ textAlign: 'center', marginBottom: 16 }}>
              <img
                src={doctor.photoUrl}
                alt={doctor.name}
                style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', border: '3px solid #eef2ff', margin: '0 auto 14px', display: 'block' }}
              />
              <h1 style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 18, color: '#1e1b4b', marginBottom: 4 }}>
                {doctor.name}
              </h1>
              <div className="t-body-sm" style={{ marginBottom: 8 }}>{doctor.qualifications}</div>
              <div style={{ display: 'inline-block', background: '#f5f3ff', color: '#6d28d9', padding: '3px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 500, fontFamily: 'DM Sans, sans-serif', marginBottom: 12 }}>
                {doctor.departmentLabel}
              </div>

              {doctor.rating != null && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                  <StarRating value={doctor.rating} />
                  <span className="t-meta">{doctor.rating} / 5</span>
                </div>
              )}

              {/* Mode badge */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                {doctor.availableOnline && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, background: '#ecfeff', color: '#0891b2', border: '1px solid #a5f3fc', fontFamily: 'DM Sans, sans-serif' }}>
                    📱 Online
                  </span>
                )}
                {doctor.availableWalkIn && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', fontFamily: 'DM Sans, sans-serif' }}>
                    🏥 In-person
                  </span>
                )}
              </div>
            </div>

            {/* CTA card */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span className="t-body-sm">Consultation fee</span>
                <span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 16, color: '#1e1b4b' }}>₹{doctor.fee ?? '—'}</span>
              </div>
              <button className="btn-primary" style={{ marginBottom: 10 }} onClick={handleBook}>
                Book Appointment
              </button>
              <button
                onClick={handleSecondOpinion}
                style={{
                  width: '100%', padding: 11, borderRadius: 10, border: '1.5px solid #e0e7ff',
                  background: 'none', fontSize: 14, fontWeight: 600, color: '#6366f1',
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'background .15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                🔍 Get Second Opinion
              </button>
            </div>
          </div>

          {/* Right: details */}
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 className="t-h3">About</h3>
              <InfoRow icon="🎓" label="Qualifications" value={doctor.qualifications} />
              <InfoRow icon="💼" label="Experience" value={`${doctor.yearsExperience} years`} />
              <InfoRow icon="🏥" label="Department" value={doctor.departmentLabel} />
              {doctor.clinic && <InfoRow icon="📍" label="Clinic" value={doctor.clinic} />}
              {doctor.distanceKm != null && <InfoRow icon="📏" label="Distance" value={`${doctor.distanceKm} km`} />}
              {doctor.languages?.length > 0 && (
                <InfoRow icon="🗣️" label="Languages" value={doctor.languages.join(', ')} />
              )}
            </div>

            {doctor.bio && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 className="t-h3">Biography</h3>
                <p className="t-body-sm" style={{ lineHeight: 1.7, margin: 0 }}>{doctor.bio}</p>
              </div>
            )}

            {/* ── Publications & Content ────────────────────────── */}
            <PublicationsSection doctorId={doctorId} />

            {doctor.availableOnline && (
              <div className="card" style={{ background: '#ecfeff', border: '1px solid #a5f3fc' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 22 }}>📱</span>
                  <div>
                    <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13.5, color: '#0e7490', marginBottom: 3 }}>
                      Available online now
                    </div>
                    <div className="t-meta" style={{ color: '#0891b2' }}>
                      {doctor.nextOnlineInMins != null
                        ? `Next available in ${doctor.nextOnlineInMins} mins`
                        : 'Check schedule for next slot'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </main>
    </div>
  );
};

export default DoctorProfile;